import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthorizationFacade } from '@features/authorization/facades/authorization.facade';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { isLiveContractLineItem } from '@kerberos-compliance/compliance-shared-ts/features/contract/utilities/contract-line-item-visibility.utility';
import {
  createAsyncErrorHandler,
  createErrorMetadata,
  ErrorSeverityMap,
  NotificationService,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';
import { ContractsService } from '../services/contracts.service';
import { ContractsDetailStore } from '../stores/contracts-detail.store';
import { ContractsDetailUiStore } from '../stores/contracts-detail-ui.store';

const TranslationPrefix = 'organizations.detailPage.contractsTab';

const SyncNotificationKeys = {
  error: `${TranslationPrefix}.sync.error`,
  success: `${TranslationPrefix}.sync.success`,
} as const;

@Injectable()
export class ContractsDetailFacade {
  private readonly contractsService = inject(ContractsService);

  private readonly contractsDetailStore = inject(ContractsDetailStore);

  private readonly contractsDetailUiStore = inject(ContractsDetailUiStore);

  private readonly translationService = inject(TranslationService);

  private readonly notificationService = inject(NotificationService);

  private readonly authorizationFacade = inject(AuthorizationFacade);

  private readonly asyncErrorHandler = createAsyncErrorHandler();

  private readonly syncingState = signal<boolean>(false);

  /**
   * The contracts to render, each contract's live line items ordered ahead of its soft-deleted ones so the
   * table numbers the live rows 1..n and lists revealed deletions after them.
   */
  public readonly contracts = computed(() =>
    this.contractsDetailStore.contracts().map((contract) => ({
      ...contract,
      lineItems: contract.lineItems.toSorted(
        (first, second) => Number(!isLiveContractLineItem(first)) - Number(!isLiveContractLineItem(second)),
      ),
    })),
  );

  /**
   * The work item types the organization's active contracts entitle it to, deduplicated by id. Always
   * derived from active contracts' live line items only, regardless of what a reveal toggle adds to `contracts()`.
   */
  public readonly availableWorkItemTypes = computed(() => {
    const liveMatchedWorkItemTypes = this.contractsDetailStore
      .contracts()
      .filter((contract) => contract.isActive)
      .flatMap((contract) => contract.lineItems)
      .filter((lineItem) => isLiveContractLineItem(lineItem))
      .flatMap((lineItem) => lineItem.matchedWorkItemTypes);

    return [...new Map(liveMatchedWorkItemTypes.map((workItemType) => [workItemType.id, workItemType])).values()];
  });

  public readonly loading = this.contractsDetailStore.loading;

  /**
   * Whether the contracts list is unavailable right now, either because the whole tab is loading or because
   * a reveal toggle is re-fetching just the contracts. The template gates the list, its count and the
   * controls on this single flag rather than re-deriving the combination.
   */
  public readonly contractsBusy = computed(
    () => this.contractsDetailStore.loading() || this.contractsDetailStore.contractsRefreshing(),
  );

  /** Whether contracts that are not active today are included in the list. */
  public readonly showInactiveContracts = this.contractsDetailUiStore.showInactiveContracts;

  /** Whether soft-deleted line items are included in each contract's line item table. */
  public readonly showDeletedLineItems = this.contractsDetailUiStore.showDeletedLineItems;

  /** Whether a Salesforce sync is currently in flight (drives the sync button's loading/disabled state). */
  public readonly syncing = this.syncingState.asReadonly();

  /** Whether the current user may trigger a Salesforce sync (gated by the same role as the Contracts tab). */
  public readonly canSync = computed(() => this.authorizationFacade.hasRole(NamedRoleMap.InternalUser));

  /**
   * Loads the contracts for an organization, then expands the active contract panels. Switching organization
   * resets the reveal flags first, so history is never shown implicitly; a reload for the same organization
   * (a toggle or a sync refresh) keeps them. Failures are reported through the shared async error handler
   * (Sentry). A load superseded by a newer fetch commits nothing and leaves the busy flags to it.
   *
   * @param {UUID | undefined} organizationId - The organization to load data for.
   * @returns {Promise<void>} A promise that resolves once the data has been loaded.
   */
  public async loadByOrganizationId(organizationId: UUID | undefined): Promise<void> {
    if (!organizationId) {
      return;
    }
    this.contractsDetailUiStore.scopeToOrganization(organizationId);
    this.clearState();

    const generation = this.contractsDetailStore.startContractsFetch();
    this.contractsDetailStore.setLoadingState(true);
    try {
      await this.asyncErrorHandler(() => this.fetchContracts(organizationId, generation), {
        metadata: createErrorMetadata(
          'ContractsDetailFacade',
          'Error loading contracts for selected organization',
          ErrorSeverityMap.High,
        ),
        rethrow: true,
      });
    } finally {
      this.contractsDetailStore.endContractsFetch(generation);
    }
  }

  /**
   * Triggers a (re-)synchronization of the organization's contracts from Salesforce, then refreshes the tab.
   * The call is long-running, so it is single-flight: repeated invocations are ignored while one is in flight.
   * When the sync succeeds a success notification is shown and the contracts list is reloaded; a failure of that
   * reload is reported to Sentry but does not change the success outcome. When the sync itself fails a localized
   * error notification is shown (and the error is reported to Sentry by the shared async error handler).
   *
   * @param {UUID | undefined} organizationId - The organization to re-sync.
   * @returns {Promise<void>} A promise that resolves once the sync (and subsequent refresh) completes.
   */
  public async syncFromSalesforce(organizationId: UUID | undefined): Promise<void> {
    if (!organizationId || this.syncingState()) {
      return;
    }

    this.syncingState.set(true);
    try {
      await this.asyncErrorHandler(() => this.contractsService.syncFromSalesforce(organizationId), {
        metadata: createErrorMetadata(
          'ContractsDetailFacade',
          `Error syncing contracts from Salesforce for organization ${organizationId}`,
          ErrorSeverityMap.High,
        ),
        userMessage: this.translationService.getTranslation(SyncNotificationKeys.error),
        rethrow: true,
      });
    } catch {
      return;
    } finally {
      this.syncingState.set(false);
    }

    // The sync succeeded: notify, then refresh the list. A refresh failure is reported to Sentry by
    // loadByOrganizationId and must not surface as a misleading "sync failed" notification.
    this.notificationService.success(this.translationService.getTranslation(SyncNotificationKeys.success));
    try {
      await this.loadByOrganizationId(organizationId);
    } catch {
      // Already reported to Sentry by loadByOrganizationId; a failed refresh must not undo the success notice.
    }
  }

  /**
   * Includes or excludes contracts that are not active today, then re-fetches the contracts. Each call
   * triggers exactly one request; the other reveal flag and the rest of the tab are left untouched.
   *
   * @param {boolean} shouldShow - Whether to include inactive contracts.
   * @returns {Promise<void>} A promise that resolves once the reloaded data has been stored.
   */
  public async setShowInactiveContracts(shouldShow: boolean): Promise<void> {
    this.contractsDetailUiStore.setShowInactiveContracts(shouldShow);
    await this.reloadContracts();
  }

  /**
   * Includes or excludes soft-deleted line items, then re-fetches the contracts. Each call triggers exactly
   * one request; the other reveal flag and the rest of the tab are left untouched.
   *
   * @param {boolean} shouldShow - Whether to include soft-deleted line items.
   * @returns {Promise<void>} A promise that resolves once the reloaded data has been stored.
   */
  public async setShowDeletedLineItems(shouldShow: boolean): Promise<void> {
    this.contractsDetailUiStore.setShowDeletedLineItems(shouldShow);
    await this.reloadContracts();
  }

  /**
   * Checks whether a contract panel is expanded.
   *
   * @param {UUID} contractId - The contract ID to check.
   * @returns {boolean} True if the contract panel is expanded.
   */
  public isExpanded(contractId: UUID): boolean {
    return this.contractsDetailUiStore.isExpanded(contractId);
  }

  /**
   * Handles a collapse panel expand/collapse change.
   *
   * @param {UUID} contractId - The contract ID being toggled.
   * @param {boolean} isExpanded - Whether the panel is now expanded.
   * @returns {void} This method does not return a value.
   */
  public onExpandChange(contractId: UUID, isExpanded: boolean): void {
    this.contractsDetailUiStore.setExpanded(contractId, isExpanded);
  }

  /**
   * Expands every currently loaded contract panel, regardless of its active state or any prior manual
   * collapse. Reads the contracts already in the store rather than re-fetching.
   *
   * @returns {void} This method does not return a value.
   */
  public expandAll(): void {
    this.contractsDetailUiStore.expandAll(this.contractsDetailStore.contracts().map((contract) => contract.id));
  }

  /**
   * Collapses every currently loaded contract panel, regardless of its active state or any prior manual
   * expansion. Reads the contracts already in the store rather than re-fetching.
   *
   * @returns {void} This method does not return a value.
   */
  public collapseAll(): void {
    this.contractsDetailUiStore.collapseAll(this.contractsDetailStore.contracts().map((contract) => contract.id));
  }

  /**
   * Clears all data and UI state. Called before every load. The reveal flags are scoped to the organization
   * and deliberately survive this.
   *
   * @returns {void} This method does not return a value.
   */
  public clearState(): void {
    this.contractsDetailStore.clearAll();
    this.contractsDetailUiStore.clearAll();
  }

  /**
   * Re-fetches only the contracts for the organization already in scope. The reveal flags change nothing
   * about the available work item types, so that request is not repeated and its section is left standing
   * instead of blanking. Failures are reported to Sentry and swallowed: a failed reveal must not break the
   * toggle.
   *
   * Overlapping toggles are settled by fetch generation rather than by refusing the second one: turning a
   * toggle away would leave the checkbox and the list disagreeing, so every toggle fetches and only the
   * newest response is committed.
   *
   * @returns {Promise<void>} A promise that resolves once the contracts have been re-fetched.
   */
  private async reloadContracts(): Promise<void> {
    const organizationId = this.contractsDetailUiStore.organizationId();
    if (!organizationId) {
      return;
    }

    const generation = this.contractsDetailStore.startContractsFetch();
    this.contractsDetailStore.setContractsRefreshing(true);
    try {
      await this.asyncErrorHandler(() => this.fetchContracts(organizationId, generation), {
        metadata: createErrorMetadata(
          'ContractsDetailFacade',
          'Error reloading contracts for selected organization',
          ErrorSeverityMap.High,
        ),
      });
    } finally {
      this.contractsDetailStore.endContractsFetch(generation);
    }
  }

  /**
   * Fetches the contracts with the reveal flags as they currently stand and stores them, applying the
   * active/inactive default expand state only to contracts not seen before - so a reveal toggle's refetch
   * never undoes a manual expand/collapse of a contract already on screen.
   *
   * @param {UUID} organizationId - The organization to load contracts for.
   * @param {number} generation - The fetch generation, used to discard a response that has been superseded.
   * @returns {Promise<void>} A promise that resolves once the contracts have been stored.
   */
  private async fetchContracts(organizationId: UUID, generation: number): Promise<void> {
    const contracts = await this.contractsService.getContractDetails(organizationId, {
      shouldIncludeInactive: this.contractsDetailUiStore.showInactiveContracts(),
      shouldIncludeDeletedLineItems: this.contractsDetailUiStore.showDeletedLineItems(),
    });

    // Another toggle or an organization switch started while this was in flight; that fetch is authoritative.
    if (!this.contractsDetailStore.isCurrentContractsFetch(generation)) {
      return;
    }

    this.contractsDetailStore.setContracts(contracts ?? []);
    this.contractsDetailUiStore.applyDefaultExpansion(
      (contracts ?? []).map((contract) => ({ id: contract.id, isActive: contract.isActive })),
    );
  }
}
